import * as React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IField, IProtocol, IVersione } from '../../../../../shared/interfaces';
import { RootState } from '../../../../../store';
import versioniService from '../../../../../services/versioni.service';
import CircleLoader from '../../../../Loader/CircleLoader/CircleLoader';
import { ActivityItem, DetailsRow, Text, GroupedList, GroupHeader, IActivityItemProps, IColumn, Icon, IDetailsColumnStyleProps, IDetailsColumnStyles, IDetailsRowStyles, IGroup, IGroupHeaderProps, IGroupRenderProps, Link, mergeStyleSets, SelectionMode } from '@fluentui/react';
import Utilities from '../../../../../shared/Utilities/utilities';
import { myTheme } from '../../../../../assets/styles/my-theme';
import AppUtilities from '../../../../../shared/Utilities/appUtilities';
import { CampoVersioneEF_Status } from '../../../../../shared/enums';

const { palette, semanticColors } = myTheme;

let classNames = mergeStyleSets({
    padding: {
        paddingBottom: '3px',
        paddingTop: '3px'
    },
    headerMargins: {
        marginTop: '20px',
        marginBottom: '20px'
    },
    nameText: {
        fontWeight: 'bold',
    },
    columnWidth: {
        width: '200px'
    }
});

export interface VersioniProps {
    protocol: IProtocol
}

export interface IRigaVersione {
    displayName: string,
    displayValue: string,
    status: CampoVersioneEF_Status

}


const Versioni: React.FunctionComponent<VersioniProps> = (props) => {

    const selectCurrentAoo = (state: RootState) => state.sessionReducer.currentAoo;
    const currentAoo = useSelector(selectCurrentAoo);
    const selectCurrentUor = (state: RootState) => state.sessionReducer.currentUor;
    const currentUor = useSelector(selectCurrentUor);
    const selectCampiProtocolloFields = (state: RootState) => state.sessionReducer.campiProtocolloViewFields;
    const campiProtocolloFields = useSelector(selectCampiProtocolloFields);

    const [loading, setLoading] = useState(false);
    const [versioniGroupedList, setVersioniGroupedList] = useState<{ items: any[], groups: IGroup[] }[]>([]);

    const columns: IColumn[] = [
        {
            key: 'status',
            name: 'status',
            iconName: 'Page',
            isIconOnly: true,
            fieldName: 'status',
            minWidth: 24,
            onRender: (item) => {
                switch (item.status) {
                    case CampoVersioneEF_Status.Modified:
                        return <Icon iconName="Edit" />

                    case CampoVersioneEF_Status.Deleted:
                        return <Icon iconName="Cancel" />

                    case CampoVersioneEF_Status.New:
                        return <Icon iconName="Add" />

                    default:
                        return <React.Fragment></React.Fragment>

                }
            }
        },
        {
            key: "displayName",
            name: "displayName",
            fieldName: "displayName",
            minWidth: 300,
            className: classNames.nameText,
            isResizable: true
        },
        {
            key: "displayValue",
            name: "displayValue",
            fieldName: "displayValue",
            minWidth: 300,
            isMultiline: true,

        }
    ]


    useEffect(() => {
        if (currentAoo && props.protocol) {
            getVersioni();
        }
    }, [props.protocol, currentAoo]);

    const groupProps: IGroupRenderProps = {
        isAllGroupsCollapsed: true,
        onRenderHeader: (props?: IGroupHeaderProps): JSX.Element => (
            <GroupHeader onRenderTitle={onRenderTitle} {...props} className={`${classNames.padding} ${classNames.headerMargins}`} />
        )

    }

    const getVersioni = () => {
        setLoading(true);

        versioniService.getVersioni(currentAoo!.id, props.protocol!.protocolId!, currentUor?.id)
            .then(response => {
                let versioniList = remapVersioni(response);
                setVersioniGroupedList(versioniList);
                setLoading(false);
            })
            .catch(error => {
                console.error("Errore nel recupero delle versioni", error);
                setLoading(false);
            });

    }


    const remapVersioni = (response: IVersione[]) => {

        let mapping = response.map(
            (versione) => {
                return {
                    items: remapCampi(versione),
                    groups: [
                        {
                            key: versione.numeroVersione.toString(),
                            name: 'Versione ' + versione.numeroVersione.toString(),
                            startIndex: 0,
                            count: versione.campi.length,
                            data: versione,
                            level: 0
                        } as IGroup]
                }
            }
        );

        return mapping;
    }


    const remapCampi = (versione: IVersione) => {
        let campiProtocollo = [...campiProtocolloFields!]
        let mapping = versione.campi.map(
            (campo) => {
                let obj: IRigaVersione = {
                    displayName: campo.internalName,
                    displayValue: campo.internalValue ? campo.internalValue.toString() : '-',
                    status: campo.status
                }

                let fieldIndex = campiProtocollo.findIndex(el => el.internalName === campo.internalName);
                if (fieldIndex >= 0) {
                    let field = { ...campiProtocollo[fieldIndex], internalValue: campo.internalValue }
                    obj = {
                        displayName: field.displayName,
                        displayValue: AppUtilities.getDisplayValueFromFieldControlType(field),
                        status: campo.status
                    }

                }
                return obj;
            }
        );

        return mapping;
    }




    const onRenderTitle = (props?: IGroupHeaderProps): JSX.Element => {

        const myHeader = {
            name: props?.group?.name,
            numero: props?.group?.data.numeroVersione,
            utenteModifica: AppUtilities.FieldFormatUserInternalValue(props?.group?.data.utenteModifica, false),
            data: Utilities.FormatDate(props?.group?.data.dataVersione, false)
        }
        const item: IActivityItemProps = {
            activityDescription: [
                <div>
                    <Link key={2} className={classNames.nameText}>{myHeader.name}</Link>
                    <div key={1}>{myHeader.utenteModifica}</div>
                </div>
            ],
            timeStamp: [
                <div key={1}>{myHeader.data}</div>
            ]
        }
        return (
            <ActivityItem {...item} />
        )
    }


    const onRenderCell = (nestingDepth?: number, item?: IRigaVersione, itemIndex?: number, group?: IGroup): React.ReactNode => {

        if (item) {
            let cellBackgroundColor: string | undefined = undefined;
            switch (item.status) {
                case CampoVersioneEF_Status.Modified:
                    cellBackgroundColor = semanticColors.warningBackground;
                    break;

                case CampoVersioneEF_Status.Deleted:
                    cellBackgroundColor = semanticColors.errorBackground;
                    break;

                case CampoVersioneEF_Status.New:
                    cellBackgroundColor = semanticColors.successBackground;
                    break;

            }

            let detailsRowStyles: Partial<IDetailsRowStyles> = {
                root: {
                    display: 'flex',
                    width: '100%',
                    backgroundColor: cellBackgroundColor,
                    flex: 1,
                    '& .ms-DetailsRow-fields': {
                        width: '100%'
                    },
                    '& .ms-DetailsRow-cell:first-child': {
                        flex: '0 0 24px',
                        display: 'flex',
                        paddingRight: 0,
                        alignSelf: 'center',
                        alignItems: 'center'
                    },
                    '& .ms-DetailsRow-cell:nth-child(2)': {
                        flex: '0 1 200px',
                        alignSelf: 'center',
                        alignItems: 'center'

                    },
                    '& .ms-DetailsRow-cell:nth-child(3)': {
                        flex: '1 1 200px',
                        alignSelf: 'center',
                        alignItems: 'center'

                    }
                }
            }

            return (

                <DetailsRow
                    columns={columns}
                    groupNestingDepth={nestingDepth}
                    item={item}
                    itemIndex={itemIndex!}
                    selectionMode={SelectionMode.none}
                    group={group}
                    styles={detailsRowStyles}
                />

                
            );
        }
    }


    return (

        <div className={`flex flex-column position-relative`}>
            <CircleLoader loaderClass='loader-large' loading={loading} />
            <div className={'my-scroll-div position-relative'}>
                <div className={`my-component-content`}>
                    {versioniGroupedList.map(
                        (versione, index) => (

                            <GroupedList
                                key={index}
                                items={versione.items}
                                onRenderCell={onRenderCell}
                                selectionMode={SelectionMode.none}
                                groups={versione.groups}
                                groupProps={groupProps}
                            />
                        )
                    )}

                    {

                        versioniGroupedList.length === 0 &&

                        <Text block style={{ paddingTop: 10 }}>Nessuna versione presente.</Text>

                    }
                </div>
            </div>
        </div>

    );

}

export default Versioni;